/**
 * 변수 속성 목록 편집 팝업.
 *
 * 공통코드 도움창(SinglePopup) 과 같은 모양을 내려고 같은 그리드(GridV2/SnGrid)를 쓴다.
 * 다만 도움창은 DB를 조회해 "고르는" 화면이라 검색칸과 조회 버튼이 있는데,
 * 이쪽은 사용자가 직접 값을 넣는 화면이라 조회할 대상이 없다. 그래서 검색 영역은 빼고
 * 도움창의 행추가 버튼 자리(+)만 남겼다.
 *
 * 컬럼 메타(sys_formgridcolumn)가 DB에 없으므로 columnConfig를 코드로 만들어 넘긴다.
 *
 * 반환 규약: CommonModal의 handleConfirm은 getSelectedRow가 "비어 있지 않은 배열"일
 * 때만 결과로 인정한다(CommonModal.tsx:257-262). 변수를 전부 지운 상태(빈 배열)도
 * 정상적인 결과이므로 getSelectedValue로 객체를 돌려줘 그 검사를 피한다.
 * 단 { code: ... } 형태는 별도 분기를 타므로(:161-165) code 키는 쓰지 않는다.
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { type ColumnConfig } from "@shared/components/GridV2/SnGridTypes";
import { getCodeData } from "@shared/utils/CommonUtils";
import { DYNAMIC_DEFAULT_GROUPS } from "@shared/components/SnHtmlTemplateEditor";
import { TYPE_CD_CLAS, type VarProps } from "./VarPropsPopup";

interface Props {
  /** 편집을 시작할 목록 */
  defaultValue?: VarProps[];
  /** 읽기 전용 — 행 추가/삭제와 셀 편집을 막는다 */
  readOnly?: boolean;
}

/**
 * ColumnConfig는 DB에서 읽은 sys_formgridcolumn 행을 전제로 cd_firm/cd_menu/cd_user를
 * 필수로 요구하는데, 이 목록은 코드로 만든 것이라 해당 없다.
 * 컬럼 정의 생성에는 쓰이지 않으므로 빈 값을 채운다.
 */
const COLUMN_BASE = { cd_firm: "", cd_menu: "", cd_user: "", cd_grid: "grid1" };

/**
 * Type 컬럼의 선택 목록은 공통코드(mas_codel cd_clas = TYPE_CD_CLAS)에서 읽는다.
 *
 * GridV2의 SnComboBox는 js_combo만 읽는다(SnColumnsCreate.ts:376-380). cd_clas 폴백이
 * 있는 구버전 그리드(Grid/SnGrid_ColumnDefs.ts:236-250)와 달리 cd_clas만 넣으면 목록이 빈다.
 * 평소엔 백엔드 getInitForm이 fn_cd_clas_sql_to_jsonb로 js_combo를 채워주지만
 * 이 columnConfig는 코드로 만든 것이라 채워줄 주체가 없어 직접 조회해 넣는다.
 */
const buildColumns = (typeOptions: any[]): ColumnConfig[] => [
  {
    ...COLUMN_BASE,
    cd_column: "id",
    nm_column: "ID",
    tp_column: "SnTextBox",
    qt_width: 160,
    fg_visible: "Y",
    yn_required: "Y",
    no_seq: 1,
  },
  {
    ...COLUMN_BASE,
    cd_column: "name",
    nm_column: "ID명",
    tp_column: "SnTextBox",
    qt_width: 150,
    fg_visible: "Y",
    no_seq: 2,
  },
  {
    ...COLUMN_BASE,
    cd_column: "type",
    nm_column: "Type",
    tp_column: "SnComboBox",
    cd_clas: TYPE_CD_CLAS,
    // ColumnConfig.js_combo는 string 타입이다. DB에서 jsonb가 문자열로 내려오는 걸
    // 전제한 타입이고, SnColumnsCreate가 문자열이면 JSON.parse 한다(:378-379).
    js_combo: JSON.stringify(typeOptions),
    qt_width: 130,
    fg_visible: "Y",
    no_seq: 3,
  },
  {
    ...COLUMN_BASE,
    cd_column: "default",
    nm_column: "Default",
    tp_column: "SnTextBox",
    qt_width: 190,
    fg_visible: "Y",
    no_seq: 4,
  },
  {
    ...COLUMN_BASE,
    cd_column: "format",
    nm_column: "Format",
    tp_column: "SnTextBox",
    qt_width: 160,
    fg_visible: "Y",
    no_seq: 5,
  },
];

/** 팝업이 다루는 5개 항목만 남긴다 — 그리드가 붙이는 KeyField/state는 저장하지 않는다 */
const toVarProps = (row: any): VarProps => ({
  id: (row?.id ?? "").toString().trim(),
  name: (row?.name ?? "").toString().trim(),
  type: (row?.type ?? "").toString().trim(),
  default: (row?.default ?? "").toString().trim(),
  format: (row?.format ?? "").toString().trim(),
});

const VarPropsListPopup = forwardRef(
  ({ defaultValue, readOnly }: Props, ref) => {
    const gridRef = useRef<any>(null);
    /**
     * Type 셀의 선택 목록(agRichSelectCellEditor)이 그려질 자리.
     *
     * 지정하지 않으면 SnGrid가 #ag-grid-popup-root를 찾다 실패하고 <body>로 떨어진다
     * (SnGrid.tsx:2758-2763 — 그 엘리먼트는 앱 어디에서도 만들지 않는다).
     * <body>는 모달 바깥이라 MUI Dialog의 focus trap이 포커스를 도로 뺏어가고
     * z-index도 모달 아래라, 셀을 눌러도 목록이 뜨자마자 닫혀 클릭이 안 먹는 것처럼 보인다.
     * 팝업 안의 엘리먼트를 popupParent로 물려 해결한다(SYS_ADDINFO_POP_01.tsx:117-120과 같은 방식).
     */
    const popupRootRef = useRef<HTMLDivElement>(null);
    const [popupParentEl, setPopupParentEl] = useState<HTMLElement | null>(
      null
    );

    useEffect(() => {
      setPopupParentEl(popupRootRef.current);
    }, []);

    // 작성 도움말 접기 — 평소엔 접어둬서 그리드가 넓게 쓰이도록 한다
    const [guideOpen, setGuideOpen] = useState(false);

    // 공통코드를 받아오기 전에는 빈 목록으로 컬럼을 만들어 둔다.
    // 조회가 끝나면 columnConfig가 새 배열이 되어 SnGrid가 컬럼을 다시 만든다(SnGrid.tsx:395).
    const [columns, setColumns] = useState<ColumnConfig[]>(() =>
      buildColumns([])
    );

    useEffect(() => {
      let alive = true;
      const loadTypeOptions = async () => {
        const data = await getCodeData(TYPE_CD_CLAS, "");
        if (!alive) return;
        setColumns(buildColumns(Array.isArray(data) ? data : []));
      };
      loadTypeOptions();
      return () => {
        alive = false;
      };
    }, []);

    useImperativeHandle(ref, () => ({
      getSelectedValue: () => {
        // 편집 중인 셀이 있으면 값을 확정시킨 뒤 읽는다
        gridRef.current?.stopEditing?.();

        // SnGrid의 ref는 AgGridReact를 그대로 넘겨주지 않는다. 노출하는 건
        // useImperativeHandle에 나열된 메서드뿐이고 api는 그중에 없다(SnGrid.tsx:905-1038).
        // gridRef.current.api로 접근하면 undefined라 한 행도 못 읽는다.
        const rows: any[] = [];
        gridRef.current?.forEachNode((node: any) => {
          if (node.data) rows.push(node.data);
        });

        // ID 없는 행은 저장해도 쓸 수 없으므로 버린다
        return {
          varProps: rows.map(toVarProps).filter((r) => r.id !== ""),
        };
      },
    }));

    return (
      <div
        ref={popupRootRef}
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* 도움창의 행추가 버튼 자리. 조회할 대상이 없어 검색칸/조회 버튼은 두지 않는다. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginBottom: 6,
          }}
        >
          {!readOnly && (
            <>
              <Tooltip title="행 추가">
                <IconButton
                  size="small"
                  onClick={() => gridRef.current?.addRow({})}
                  sx={BTN_SX}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="행 삭제">
                <IconButton
                  size="small"
                  onClick={() => gridRef.current?.deleteSelectedRows()}
                  sx={BTN_SX}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <button
            type="button"
            onClick={() => setGuideOpen((v) => !v)}
            style={GUIDE_BTN}
          >
            {guideOpen ? "▲" : "▼"} 작성 도움말
          </button>
        </div>

        {/* 작성 도움말 — 접어둘 수 있게 해서 평소엔 그리드가 넓게 쓰이도록 한다 */}
        {guideOpen && (
          <div style={GUIDE_BOX}>
            <div style={{ marginBottom: 6 }}>
              본문에는 <b>@%ID@%</b> 형태로 넣습니다. ID가 <b>CD_DEPT</b>면 본문에{" "}
              <b>@%CD_DEPT@%</b> 라고 쓰면 됩니다.
            </div>

            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr>
                  <th style={GUIDE_TH}>Default에 이렇게 쓰면</th>
                  <th style={GUIDE_TH}>같은 뜻</th>
                  <th style={GUIDE_TH}>지금 값</th>
                </tr>
              </thead>
              <tbody>
                {/* 예약어 목록은 실제 치환을 담당하는 쪽에서 그대로 가져온다.
                    여기 따로 적어두면 한쪽만 고쳤을 때 안내와 동작이 어긋난다. */}
                {DYNAMIC_DEFAULT_GROUPS.map((g) => (
                  <tr key={g.keys[0]}>
                    <td style={GUIDE_TD}>
                      <b>{g.keys[0]}</b>
                    </td>
                    <td style={{ ...GUIDE_TD, color: "#777" }}>
                      {g.keys.slice(1).join(", ") || "-"}
                    </td>
                    <td style={GUIDE_TD}>{g.get() || "(로그인 정보 없음)"}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: 6, color: "#555" }}>
              위에 없는 값은 입력한 그대로 쓰입니다(예: 시작일 <b>20260810</b>).
              날짜 표기는 Format으로 정합니다 — <b>YYYY</b>(연) <b>MM</b>(월){" "}
              <b>DD</b>(일), <b>대소문자를 구분</b>합니다.
            </div>
          </div>
        )}

        <div style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
          <SnGrid
            ref={gridRef}
            columnConfig={columns}
            rowData={defaultValue ?? []}
            etcProps={{
              rowSelection: "multiple",
              isEditable: !readOnly,
              ischeckboxSelection: !readOnly,
              // 목록이 모달 안에서 뜨도록 (위 popupRootRef 주석 참고)
              popupParent: popupParentEl,
            }}
          />
        </div>
      </div>
    );
  }
);

/** 도움창의 + 버튼과 같은 각진 아웃라인 모양 */
const BTN_SX = {
  border: "1px solid #90caf9",
  borderRadius: "4px",
  color: "#1976d2",
  width: 30,
  height: 30,
};

const GUIDE_BTN: React.CSSProperties = {
  border: "1px solid #d0d5dd",
  borderRadius: 4,
  background: "#fff",
  color: "#555",
  fontSize: 12,
  height: 30,
  padding: "0 10px",
  cursor: "pointer",
};

const GUIDE_BOX: React.CSSProperties = {
  border: "1px solid rgb(196 196 196)",
  borderRadius: 4,
  background: "#fafbfc",
  padding: "8px 10px",
  marginBottom: 6,
  fontSize: 12,
  lineHeight: 1.6,
  flex: "0 0 auto",
};

const GUIDE_TH: React.CSSProperties = {
  textAlign: "left",
  fontSize: 12,
  fontWeight: 700,
  padding: "4px 8px",
  borderBottom: "1px solid rgb(196 196 196)",
  whiteSpace: "nowrap",
};

const GUIDE_TD: React.CSSProperties = {
  fontSize: 12,
  padding: "3px 8px",
  borderBottom: "1px solid #eee",
};

export default VarPropsListPopup;
